import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import '../css/localMode.css';
import { listCameras, saveCamera, getCredentials, removeCamera, clearAll, uuid } from '../utils/localCredStore';
import { playNativeRtsp } from '../utils/localRtspBridge';
import LocalAppTab from './localMode/LocalAppTab';
import LocalCamerasTab, { emptyLocalCameraForm } from './localMode/LocalCamerasTab';
import LocalAlertsTab from './localMode/LocalAlertsTab';
import { useServerUnreachable, probeServer } from '../utils/serverReachability';

const TABS = [
  { key: 'app',     label: 'Local App', icon: '🏠' },
  { key: 'cameras', label: 'Cameras',   icon: '📷' },
  { key: 'alerts',  label: 'Alerts',    icon: '🔔' },
];

// The "Local App" tab opens the full app on the box. With the server
// unreachable that's not an option worth offering — only cameras (straight
// from the LAN) and saved alerts work, so those are all that's shown.
//
// `embedded`: rendered inside the logged-in app shell (sidebar's Local Mode
// entry while the server is unreachable) — just the cameras and saved alerts,
// tabs at the top instead of a fixed bottom bar that would cover the shell,
// and no "back" link since the sidebar is right there.
const EMBEDDED_TABS = TABS.filter(t => t.key !== 'app');

export default function LocalModePage({ embedded = false }) {
  const unreachable = useServerUnreachable();
  const tabs = embedded || unreachable ? EMBEDDED_TABS : TABS;
  const [tab, setTab] = useState(embedded || unreachable ? 'cameras' : 'app');

  useEffect(() => { probeServer(); }, []);
  useEffect(() => {
    if (unreachable && tab === 'app') setTab('cameras');
  }, [unreachable, tab]);
  // Reached from the no-internet banner while still logged in — go back to
  // the app, not the sign-in page (the session was never ended).
  const isAuthenticated = useSelector(s => s.auth.isAuthenticated);

  const [cameras, setCameras] = useState(() => listCameras());
  const [form,     setForm]     = useState(emptyLocalCameraForm);
  const [showPw,   setShowPw]   = useState(false);
  const [error,    setError]    = useState('');
  const [promptFor,setPromptFor]= useState(null);  // camera id awaiting inline credential entry

  const refresh = () => setCameras(listCameras());

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.host.trim()) { setError('Camera IP / host is required'); return; }
    const camera = {
      id: uuid(),
      name: form.name.trim() || form.host.trim(),
      protocol: form.protocol,
      host: form.host.trim(),
      port: form.port.trim(),
      path: form.path.trim(),
      transport: form.transport,
    };
    const creds = { username: form.username.trim(), password: form.password };
    const { remembered } = await saveCamera({ ...camera, ...creds, remember: form.remember });
    refresh();
    setForm(emptyLocalCameraForm);
    setShowPw(false);
    if (form.remember && !remembered) {
      setError('Camera added, but credentials could not be remembered on this device (secure storage unavailable over this connection) — you\'ll need to re-enter them next time.');
    }
  };

  const handleView = async (cam) => {
    setError('');
    if (cam.hasSavedCredentials) {
      const creds = await getCredentials(cam.id);
      playNativeRtsp(cam, creds);
    } else {
      setPromptFor(cam.id);
    }
  };

  const handleInlineConnect = (cam, username, password) => {
    setPromptFor(null);
    playNativeRtsp(cam, { username, password });
  };

  const handleForget = (id) => {
    removeCamera(id);
    refresh();
  };

  const handleClearAll = async () => {
    if (!window.confirm('Remove all saved local cameras and stored credentials from this device?')) return;
    await clearAll();
    refresh();
  };

  const tabBar = (
    <nav className="lm-tabbar">
      {tabs.map(t => (
        <button
          key={t.key}
          className={`lm-tabbar-btn${tab === t.key ? ' lm-tabbar-btn-active' : ''}`}
          onClick={() => setTab(t.key)}
        >
          <span className="lm-tabbar-icon">{t.icon}</span>
          <span>{t.label}</span>
        </button>
      ))}
    </nav>
  );

  return (
    <div className={`lm-root${embedded ? ' lm-embedded' : ''}`}>
      <div className="lm-wrap">
        <div className="lm-header">
          <div>
            <h1>Local Mode</h1>
            <p>{embedded
              ? 'Server unreachable — view cameras directly on this network and alerts saved on this device.'
              : 'Connect directly to your Parvekshak system on this network — faster, and works when the internet is slow. You\'ll sign in with your usual account.'}</p>
          </div>
          {!embedded && (
            <Link to={isAuthenticated ? '/' : '/login'} className="lm-back-link">
              {isAuthenticated ? '← Back to App' : '← Back to Sign In'}
            </Link>
          )}
        </div>

        {embedded && tabBar}

        <div className="lm-tab-content">
          {tab === 'app' && <LocalAppTab />}
          {tab === 'cameras' && (
            <LocalCamerasTab
              cameras={cameras}
              form={form} setForm={setForm}
              showPw={showPw} setShowPw={setShowPw}
              error={error}
              promptFor={promptFor} setPromptFor={setPromptFor}
              handleAddSubmit={handleAddSubmit}
              handleView={handleView}
              handleInlineConnect={handleInlineConnect}
              handleForget={handleForget}
              handleClearAll={handleClearAll}
            />
          )}
          {tab === 'alerts' && <LocalAlertsTab />}
        </div>
      </div>

      {!embedded && tabBar}
    </div>
  );
}
